import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import Link from 'next/link';

import { Button } from '@/components/atoms';
import { Timezone } from '@/components/molecules/main-header/types';
import { MatchupStatusEnum } from '@/domain/constants/constants';
import { LINEUP_PERMISSIONS, SCORES_PERMISSIONS } from '@/entities/PermissionRoles';
import { CaptainLineupsAndScoresPermissions } from '@/infrastructure/network/dto/SeasonDivisionPool';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { DateUtil } from '@/utils/date';

interface UpcomingMatchCardProps {
	plannedStartDate?: string;
	homeTeam: string;
	homeTeamUuid: string;
	awayTeam: string;
	awayTeamUuid: string;
	matchupStatus: string;
	time?: string;
	venue?: string;
	buttonText?: string;
	plannedEndDate?: string;
	completed?: boolean;
	href: string;
	permission: CaptainLineupsAndScoresPermissions;
	timezone?: Timezone | null;
}

export const UpcomingMatchCard = ({
	plannedStartDate,
	homeTeam,
	awayTeam,
	matchupStatus,
	venue,
	plannedEndDate,
	href,
	permission,
	homeTeamUuid,
	awayTeamUuid,
	timezone
}: UpcomingMatchCardProps) => {
	const { teamLeagueTimeZone: tlTimeZone } = useOrganizationStore();
	const ianaTzIdentifier = timezone?.ianaTzIdentifier || tlTimeZone?.ianaTzIdentifier;

	const updatedHref = timezone ? `${href}?ianaTzIdentifier=${timezone?.ianaTzIdentifier}&abbreviation=${timezone?.abbreviation}` : href;

	const localDate = dayjs.utc(plannedStartDate).tz(ianaTzIdentifier || 'UTC');
	const permissionsStore = usePermissionStore();
	const isToday = localDate.isSame(dayjs().tz(ianaTzIdentifier || 'UTC'), 'day');
	const formattedDate = isToday ? 'Today' : DateUtil.formatUTCInTimezone(plannedStartDate, 'MMMM D, YYYY', ianaTzIdentifier);
	const formattedTime = DateUtil.formatUTCInTimezone(plannedStartDate, 'h:mm A', ianaTzIdentifier);
	const formattedLineupDeadline = `by ${DateUtil.formatUTCInTimezone(plannedEndDate, 'ddd MMMM D [at] h:mmA', ianaTzIdentifier)} ${timezone?.abbreviation || tlTimeZone?.abbreviation || ''}`;

	const isTeamCaptain = permissionsStore.teamId === homeTeamUuid || permissionsStore.teamId === awayTeamUuid;
	const canSetLineup = permissionsStore.gamedayAccess || (LINEUP_PERMISSIONS.some((v) => v === permission) && isTeamCaptain);
	const canSetScores = permissionsStore.gamedayAccess || (SCORES_PERMISSIONS.some((v) => v === permission) && isTeamCaptain);

	return (
		<div className="relative flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2 text-sm shadow-sm sm:py-3 sm:pl-4 sm:pr-[22px]">
			<div className="flex flex-col">
				<div className="text-xs text-gray-500">
					{isToday ? `Today - ${localDate.format('MMMM D, YYYY')} | ${formattedTime}` : `${formattedDate} | ${formattedTime}`}{' '}
					{timezone?.abbreviation || tlTimeZone?.abbreviation || ''}
				</div>

				<div className="flex flex-col items-start gap-4 pt-3 text-sm font-semibold text-gray-700 sm:flex-row sm:gap-1">
					<div className="flex items-center">
						<span className="mr-2 inline-block size-[6px] rounded-full bg-blue-500" />
						<span>{awayTeam}</span>
						<div className="flex pl-1 text-gray-700 sm:hidden">vs</div>
					</div>

					<div className="hidden text-gray-500 sm:flex">@</div>

					<div className="flex items-center">
						<span>{homeTeam}</span>
					</div>
				</div>

				{venue ? (
					<div className="flex items-center gap-1 pt-4 text-xs text-gray-700 sm:pt-6 md:hidden">
						<FontAwesomeIcon icon={faLocationDot} className="h-[20px] w-[14px]" />
						<span className="text-gray-700">{venue}</span>
					</div>
				) : null}
			</div>

			{venue ? (
				<div className="hidden md:flex items-center justify-end gap-1 text-xs text-gray-700 w-[200px] absolute left-1/2 transform -translate-x-1/2">
					<FontAwesomeIcon icon={faLocationDot} className="h-[20px] w-[14px] shrink-0" />
					<span>{venue}</span>
				</div>
			) : null}

			<div className="flex flex-col items-center">
				{(matchupStatus === MatchupStatusEnum.UNSCHEDULED_MATCHUP_STATUS ||
					matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
					matchupStatus === MatchupStatusEnum.SCHEDULED_WAITING_FOR_HOME_TEAM_LINEUP ||
					matchupStatus === MatchupStatusEnum.SCHEDULED_WAITING_FOR_AWAY_TEAM_LINEUP) && (
					<Link href={updatedHref} prefetch={false}>
						<Button
							className="!sm:px-[60px] !h-[26px] cursor-pointer !rounded-md bg-orange-500 !px-[10px] !py-1 text-xs font-medium text-white shadow-md hover:bg-orange-700 sm:w-full disabled:cursor-not-allowed disabled:bg-gray-300"
							disabled={!canSetLineup}
							title={!canSetLineup ? "You don't have permission to set the lineup" : ''}
						>
							Set line up
						</Button>
					</Link>
				)}

				{(matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
					matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
					<Link href={updatedHref} prefetch={false}>
						<Button className="!sm:px-[60px] !h-[26px] cursor-pointer !rounded-md bg-orange-500 !px-[10px] !py-1 text-xs font-medium text-white shadow-md hover:bg-orange-700 sm:w-full">
							{!canSetScores ? 'View' : 'Enter'} scores
						</Button>
					</Link>
				)}

				{matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS && (
					<div className="flex items-center">
						<span className="inline-block size-2 rounded-full bg-[#099250]" />
						<p className="pl-2 text-[#099250]">Match completed</p>
					</div>
				)}

				{formattedLineupDeadline ? <div className="w-[80px] pt-2 text-xs text-gray-700 sm:w-full">{formattedLineupDeadline}</div> : null}
			</div>
		</div>
	);
};
